"use client"

import type React from "react"

import { Card } from "@/components/ui/card"
import { MoreVertical } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useEffect, useRef, useState } from "react"

interface LocationCardProps {
  id: number // ✅ ADD: id prop
  name: string
  imageUrl: string
  description?: string
  totalBins: number
  bins: string[]
  // ✅ FIXED: Add id field to match LocationData interface
  onEdit?: (location: {
    id: number
    name: string
    imageUrl: string
    description?: string
    totalBins: number
    bins: string[]
  }) => void
  onDelete?: () => void
}

const LocationCard: React.FC<LocationCardProps> = ({
  id, // ✅ ADD: destructure id prop
  name,
  imageUrl,
  description,
  totalBins,
  bins,
  onEdit,
  onDelete,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showBins, setShowBins] = useState(false)

  const cardRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)

  const handleCardClick = () => {
    setShowBins(!showBins)
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete?.()
    setIsMenuOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (cardRef.current && !cardRef.current.contains(target)) {
        setShowBins(false)
      }
      if (menuRef.current && !menuRef.current.contains(target)) {
        setIsMenuOpen(false)
      }
    }

    if (showBins || isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showBins, isMenuOpen])

  return (
    <button
      ref={cardRef}
      type="button"
      className="relative aspect-[16/9] lg:aspect-[3/2] 2xl:aspect-[16/10]"
      onClick={handleCardClick}
    >
      <Card
        className="p-6 rounded-lg text-card-foreground overflow-hidden group h-full relative"
        style={{
          border: `1px solid rgba(255, 128, 0, 0.3)`,
          backgroundImage: `url(${imageUrl}), linear-gradient(-180deg, rgba(255, 128, 0, 0.2), rgba(255, 128, 0, 0.1))`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/30 rounded-lg" />
        <div className="flex flex-row justify-between z-20">
          <div className="flex-initial flex-col">
            <div className="grid text-left text-sm relative space-y-1">
              <span
                className="text-xl font-bold text-white z-10 relative"
                style={{ textShadow: "1px 1px 2px rgba(0, 0, 0, 0.6)" }}
              >
                {name}
              </span>

              <div className="relative h-4">
                <span
                  className="absolute left-0 top-0 text-xs text-white transition-opacity duration-300 group-hover:opacity-0 whitespace-nowrap"
                  style={{ textShadow: "1px 1px 2px rgba(0, 0, 0, 0.6)" }}
                >
                  Total Bins: {totalBins}
                </span>

                <span
                  className="absolute left-0 top-0 text-xs text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100 whitespace-nowrap"
                  style={{ textShadow: "1px 1px 2px rgba(0, 0, 0, 0.6)" }}
                >
                  More information
                </span>
              </div>
            </div>
          </div>

          <div className="relative z-10 mt-[-10px]" ref={menuRef}>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation()
                toggleMenu()
              }}
              className="text-white hover:bg-white/20 shadow-md"
            >
              <MoreVertical className="w-5 h-5" />
            </Button>
            {isMenuOpen && (
              <div className="absolute top-[80%] right-0 bg-white border border-gray-200 rounded-lg shadow-md w-24 z-20">
                <ul className="py-1">
                  <li
                    onClick={(e) => {
                      e.stopPropagation()
                      // ✅ FIXED: Now we can pass the id
                      onEdit?.({ id, name, imageUrl, description, totalBins, bins })
                      setIsMenuOpen(false)
                    }}
                    className="px-3 py-1 text-sm text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    Edit
                  </li>
                  <li
                    onClick={handleDeleteClick}
                    className="px-3 py-1 text-sm text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    Delete
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="absolute bottom-3 z-10 left-6 right-6 overflow-hidden">
          {description && (
            <p
              className="text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ textShadow: "1px 1px 2px rgba(0, 0, 0, 0.6)" }}
            >
              {description}
            </p>
          )}
        </div>
      </Card>

      {showBins && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-white border border-gray-200 rounded-md shadow-lg p-2 z-20 min-w-[200px] text-sm">
          <strong>Bins:</strong> {bins.join(', ')}
        </div>
      )}
    </button>
  )
}

export default LocationCard